import React, { useMemo } from "react";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/CustomButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import PrintIcon from "@material-ui/icons/PictureAsPdf";
import TableRowsIcon from "@material-ui/icons/TableChart";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Typography from "@material-ui/core/Typography";

const COLORS = {
  main: "#13816F",
  headLight: "#19A58D",
  pastel1: "#CFF0EA",
  pastel2: "#E3F7F3",
  faded: "#F3FBF9",
};

const fmtMoney = (v) =>
  typeof v === "number"
    ? v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const fmtDateTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
};

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
};

export default function AgencyFee({
  title = "",
  infos = [],
  forma = {},
  report_tz = "",
  timezones = {},
  onGeneratePdf,
  onGenerateCsv,
  onBack,
}) {
  // Totales globales
  const globalTotals = useMemo(() => {
    const accum = {
      quantity: 0,
      net: 0,
      policy_fee: 0,
      iva: 0,
      total: 0,
      agency_fee: 0,
      agent_fee: 0,
      commission: 0,
    };
    infos.forEach((info) => {
      const sumList = (list, sign = 1) => {
        list.forEach((r) => {
          accum.quantity += sign * 1;
          accum.net += sign * (Number(r.revised_net_premium) || 0);
          accum.policy_fee += sign * (Number(r.policy_fee) || 0);
          accum.iva += sign * (Number(r.iva) || 0);
          accum.total += sign * (Number(r.total) || 0);
          accum.agency_fee += sign * (Number(r.agency_fee) || 0);
          accum.agent_fee += sign * (Number(r.agent_fee) || 0);
          accum.commission += sign * (Number(r.commission) || 0);
        });
      };
      sumList(info.agent?.all || [], +1);
      sumList(info.agent?.cancel || [], -1);
      sumList(info.guest?.all || [], +1);
      sumList(info.guest?.cancel || [], -1);
    });
    return accum;
  }, [infos]);

  let currentAgency = "";

  const headersField = () => {
    return (
      <TableRow style={{ background: COLORS.pastel1 }}>
        <TableCell>Policy #</TableCell>
        <TableCell>Issue Date</TableCell>
        <TableCell>From Date</TableCell>
        <TableCell>Client Name</TableCell>
        <TableCell style={{ textAlign: "right" }}>Net Prem.</TableCell>
        <TableCell style={{ textAlign: "right" }}>Policy Fee</TableCell>
        <TableCell style={{ textAlign: "right" }}>Mex IVA</TableCell>
        <TableCell style={{ textAlign: "right" }}>Total Prem.</TableCell>
        <TableCell style={{ textAlign: "right" }}>Agent Fee</TableCell>
        <TableCell style={{ textAlign: "right" }}>Agency Fee</TableCell>
        <TableCell style={{ textAlign: "right" }}>Commission</TableCell>
      </TableRow>
    );
  };

  // Renderiza tabla de pólizas
  const PolicyTable = ({ rows, negative = false }) => {
    if (!rows || rows.length === 0) return null;
    const subtotal = rows.reduce(
      (s, r) => {
        s.quantity += 1;
        s.net += Number(r.revised_net_premium) || 0;
        s.policy_fee += Number(r.policy_fee) || 0;
        s.iva += Number(r.iva) || 0;
        s.total += Number(r.total) || 0;
        s.agency_fee += Number(r.agency_fee) || 0;
        s.agent_fee += Number(r.agent_fee) || 0;
        s.commission += Number(r.commission) || 0;
        return s;
      },
      {
        quantity: 0,
        net: 0,
        policy_fee: 0,
        iva: 0,
        total: 0,
        agency_fee: 0,
        agent_fee: 0,
        commission: 0,
      }
    );
    return (
      <>
        {headersField()}
        {rows.map((row, idx) => {
          const showHeader = currentAgency !== row.producer_code && idx > 0;
          if (showHeader) {
            currentAgency = row.producer_code;
          }
          return (
            <React.Fragment key={idx}>
              {showHeader && headersField()}
              <TableRow>
                <TableCell>{row.policy_nbr}</TableCell>
                <TableCell>{fmtDateTime(row.created)}</TableCell>
                <TableCell>{fmtDate(row.effective_date)}</TableCell>
                <TableCell>{row.client_name}</TableCell>
                <TableCell style={{ textAlign: "right" }}>
                  {negative ? "-" : ""}${fmtMoney(row.revised_net_premium)}
                </TableCell>
                <TableCell style={{ textAlign: "right" }}>
                  {negative ? "-" : ""}${fmtMoney(row.policy_fee)}
                </TableCell>
                <TableCell style={{ textAlign: "right" }}>
                  {negative ? "-" : ""}${fmtMoney(row.iva)}
                </TableCell>
                <TableCell style={{ textAlign: "right" }}>
                  {negative ? "-" : ""}${fmtMoney(row.total)}
                </TableCell>
                <TableCell style={{ textAlign: "right" }}>
                  {negative ? "-" : ""}${fmtMoney(row.agent_fee)}
                </TableCell>
                <TableCell style={{ textAlign: "right" }}>
                  {negative ? "-" : ""}${fmtMoney(row.agency_fee)}
                </TableCell>
                <TableCell style={{ textAlign: "right" }}>
                  {negative ? "-" : ""}${fmtMoney(row.commission)}
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
        <TableRow style={{ background: COLORS.pastel2, fontWeight: 700 }}>
          <TableCell colSpan={3}></TableCell>
          <TableCell>
            <strong>Total: {subtotal.quantity}</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            <strong>${fmtMoney(subtotal.net)}</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            <strong>${fmtMoney(subtotal.policy_fee)}</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            <strong>${fmtMoney(subtotal.iva)}</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            <strong>${fmtMoney(subtotal.total)}</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            <strong>${fmtMoney(subtotal.agent_fee)}</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            <strong>${fmtMoney(subtotal.agency_fee)}</strong>
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            <strong>${fmtMoney(subtotal.commission)}</strong>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      {/* Hidden form fields */}
      <form id="report_form">
        {Object.entries(forma || {}).map(([k, v]) => (
          <input key={k} readOnly type="hidden" name={k} value={v} />
        ))}
      </form>
      <div className="cl-mcont">
        <GridContainer style={{ paddingTop: 20 }}>
          <GridItem xs={12}>
            <div
              className="block-flat"
              style={{ padding: 20, background: "#fff", borderRadius: 8 }}
            >
              {/* Header */}
              <GridContainer style={{ marginBottom: 16 }}>
                <GridItem xs={12} sm={6}>
                  <Typography variant="h6">
                    {report_tz
                      ? `${timezones?.[report_tz]} - ${report_tz.toUpperCase()}`
                      : ""}
                  </Typography>
                  <Typography variant="h5">{title}</Typography>
                </GridItem>
                <GridItem xs={12} sm={6}>
                  <GridContainer>
                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<PrintIcon />}
                        onClick={() => onGeneratePdf(10, "pdf")}
                        style={{ width: "100%" }}
                      >
                        PDF
                      </Button>
                    </GridItem>
                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<TableRowsIcon />}
                        onClick={() => onGenerateCsv(10, "csv")}
                        style={{ width: "100%" }}
                      >
                        CSV
                      </Button>
                    </GridItem>
                    <GridItem xs={12} sm={4}>
                      <Button
                        style={{
                          background: "#FFF",
                          borderColor: COLORS.main,
                          color: COLORS.main,
                          border: "1px solid",
                          width: "100%",
                        }}
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                      >
                        Back To Reports
                      </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
              {/* Date + Company */}
              <GridContainer style={{ marginBottom: 20 }}>
                <GridItem xs={12} sm={2}>
                  Date: {new Date().toLocaleString()}
                </GridItem>
                <GridItem
                  xs={12}
                  sm={10}
                  style={{ textAlign: "right", fontSize: 20 }}
                >
                  HDI Seguros, S.A. de C.V.
                </GridItem>
              </GridContainer>
              {/* Main table area */}
              <TableContainer component={Paper}>
                <Table>
                  <TableBody>
                    {infos.map((info, idxA) => {
                      // Totales por tipo B y C
                      const agentTotals = {
                        quantity: 0,
                        net: 0,
                        policy_fee: 0,
                        iva: 0,
                        total: 0,
                        agency_fee: 0,
                        agent_fee: 0,
                        commission: 0,
                      };
                      const guestTotals = {
                        quantity: 0,
                        net: 0,
                        policy_fee: 0,
                        iva: 0,
                        total: 0,
                        agency_fee: 0,
                        agent_fee: 0,
                        commission: 0,
                      };
                      const addListToTotals = (totals, list, sign = 1) =>
                        (list || []).forEach((r) => {
                          totals.quantity += sign * 1;
                          totals.net +=
                            sign * (Number(r.revised_net_premium) || 0);
                          totals.policy_fee +=
                            sign * (Number(r.policy_fee) || 0);
                          totals.iva += sign * (Number(r.iva) || 0);
                          totals.total += sign * (Number(r.total) || 0);
                          totals.agency_fee +=
                            sign * (Number(r.agency_fee) || 0);
                          totals.agent_fee += sign * (Number(r.agent_fee) || 0);
                          totals.commission +=
                            sign * (Number(r.commission) || 0);
                        });
                      addListToTotals(agentTotals, info.agent?.all || [], +1);
                      addListToTotals(
                        agentTotals,
                        info.agent?.cancel || [],
                        -1
                      );
                      addListToTotals(guestTotals, info.guest?.all || [], +1);
                      addListToTotals(
                        guestTotals,
                        info.guest?.cancel || [],
                        -1
                      );

                      // Total por agencia
                      const agencyTotals = {
                        quantity: agentTotals.quantity + guestTotals.quantity,
                        net: agentTotals.net + guestTotals.net,
                        policy_fee:
                          agentTotals.policy_fee + guestTotals.policy_fee,
                        iva: agentTotals.iva + guestTotals.iva,
                        total: agentTotals.total + guestTotals.total,
                        agency_fee:
                          agentTotals.agency_fee + guestTotals.agency_fee,
                        agent_fee:
                          agentTotals.agent_fee + guestTotals.agent_fee,
                        commission:
                          agentTotals.commission + guestTotals.commission,
                      };

                      return (
                        <React.Fragment key={idxA}>
                          {/* Encabezado de agencia */}
                          <TableRow>
                            <TableCell
                              colSpan={11}
                              style={{
                                background: COLORS.main,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              {info.agency?.code} — {info.agency?.name}
                            </TableCell>
                          </TableRow>
                          {/* Pólizas tipo B */}
                          <TableRow>
                            <TableCell
                              colSpan={11}
                              style={{
                                background: COLORS.headLight,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              HDI Seguros Agency (B) Policies
                            </TableCell>
                          </TableRow>
                          <PolicyTable rows={info.agent?.all || []} />
                          <PolicyTable
                            rows={info.agent?.cancel || []}
                            negative
                          />
                          {/* Totales tipo B */}
                          <TableRow style={{ background: COLORS.pastel2 }}>
                            <TableCell colSpan={3}></TableCell>
                            <TableCell>
                              <strong>
                                Agency (B) Total: {agentTotals.quantity}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agentTotals.net)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agentTotals.policy_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agentTotals.iva)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agentTotals.total)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agentTotals.agent_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agentTotals.agency_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agentTotals.commission)}
                              </strong>
                            </TableCell>
                          </TableRow>
                          {/* Pólizas tipo C */}
                          <TableRow>
                            <TableCell
                              colSpan={11}
                              style={{
                                background: COLORS.headLight,
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              HDI Seguros Agency (C) Policies
                            </TableCell>
                          </TableRow>
                          <PolicyTable rows={info.guest?.all || []} />
                          <PolicyTable
                            rows={info.guest?.cancel || []}
                            negative
                          />
                          {/* Totales tipo C */}
                          <TableRow style={{ background: COLORS.pastel2 }}>
                            <TableCell colSpan={3}></TableCell>
                            <TableCell>
                              <strong>
                                Agency (C) Total: {guestTotals.quantity}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(guestTotals.net)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(guestTotals.policy_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(guestTotals.iva)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(guestTotals.total)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(guestTotals.agent_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(guestTotals.agency_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(guestTotals.commission)}
                              </strong>
                            </TableCell>
                          </TableRow>
                          {/* Total por agencia */}
                          <TableRow style={{ background: COLORS.pastel1 }}>
                            <TableCell colSpan={3}></TableCell>
                            <TableCell>
                              <strong>
                                Agency Grand Total: {agencyTotals.quantity}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.net)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.policy_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.iva)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>${fmtMoney(agencyTotals.total)}</strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.agent_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.agency_fee)}
                              </strong>
                            </TableCell>
                            <TableCell style={{ textAlign: "right" }}>
                              <strong>
                                ${fmtMoney(agencyTotals.commission)}
                              </strong>
                            </TableCell>
                          </TableRow>
                          {/* Espaciador visual */}
                          <TableRow>
                            <TableCell
                              colSpan={11}
                              style={{ background: COLORS.faded }}
                            >
                              &nbsp;
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                    {/* Grand Total */}
                    <TableRow className="odd gradeX">
                      <TableCell colSpan={3}></TableCell>
                      <TableCell>
                        <strong>
                          HDI Seguros Grand Total: {globalTotals.quantity}
                        </strong>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <strong>${fmtMoney(globalTotals.net)}</strong>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <strong>${fmtMoney(globalTotals.policy_fee)}</strong>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <strong>${fmtMoney(globalTotals.iva)}</strong>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <strong>${fmtMoney(globalTotals.total)}</strong>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <strong>${fmtMoney(globalTotals.agent_fee)}</strong>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <strong>${fmtMoney(globalTotals.agency_fee)}</strong>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <strong>${fmtMoney(globalTotals.commission)}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Bottom Buttons */}
              <GridContainer style={{ marginTop: 16 }}>
                <GridItem xs={12} sm={6} />
                <GridItem xs={12} sm={6}>
                  <GridContainer>
                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<PrintIcon />}
                        onClick={() => onGeneratePdf(10, "pdf")}
                        style={{ width: "100%" }}
                      >
                        PDF
                      </Button>
                    </GridItem>
                    <GridItem xs={12} sm={4}>
                      <Button
                        variant="green"
                        startIcon={<TableRowsIcon />}
                        onClick={() => onGenerateCsv(10, "csv")}
                        style={{ width: "100%" }}
                      >
                        CSV
                      </Button>
                    </GridItem>
                    <GridItem xs={12} sm={4}>
                      <Button
                        style={{
                          background: "#FFF",
                          borderColor: COLORS.main,
                          color: COLORS.main,
                          border: "1px solid",
                          width: "100%",
                        }}
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                      >
                        Back To Reports
                      </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </div>
    </div>
  );
}
